import React, { useEffect, useState, useRef } from "react";
import AnnouncementDetailModal from "../../components/AnnouncementDetailModal";
import AnnouncementsTable from "../../components/announcementsTable";
import Pagination from "../../components/pagination/pagination";
import ViewLayout from "../../components/viewLayout";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";
import {
  ANNOUNCEMENTS_CHANGED_EVENT,
  getAnnouncementAuditMetadata,
  getAnnouncementId,
  markAnnouncementRead,
} from "../../utils/announcements";
import { sendAuditEvent } from "../../utils/auditTrail";

const Announcements = () => {
  const [forceUpdate, setForce] = useState(1);
  const [tableData, setTableData] = useState([]);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [ordering, setOrdering] = useState({
    order: "-",
    name: "created",
    sort: "created",
  });

  const token = getCookie("token");

  const [apiData, error, loading] = useFetch(
    "GET",
    `announcements/?ordering=${ordering.order + ordering.name}&page=${currrentPage}&page_size=${pageSize}&search=${query}`,
    token,
    false,
    forceUpdate
  );

  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);

  const defaultSorted = [
    {
      dataField: ordering.sort,
      order: ordering.order === "-" ? "desc" : "asc",
    },
  ];

  const tableProps = {
    tableData,
    defaultSorted,
  };

  useEffect(() => {
    if (apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (apiData) {
      if (apiData.count !== undefined && apiData.count !== null) {
        setDataCount(apiData.count);
      }
      if (apiData.results) {
        setTableData(
          apiData.results.map((row, index) => ({
            ...row,
            tableKey: String(
              getAnnouncementId(row) ?? `ann-${index}-${row.title || ""}-${row.created || ""}`
            ),
          }))
        );
      }
    }
  }, [apiData]);

  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, pageSize, ordering.order, ordering.name]);

  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500);
    return () => clearTimeout(timeout);
  }, [query]);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currrentPage,
  };

  useEffect(() => {
    const onAnnouncementsChanged = () => {
      refreshList();
    };
    window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onAnnouncementsChanged);
    return () => window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onAnnouncementsChanged);
  }, []);

  const refreshList = () => setForce(prev => prev + 1);

  const markRowReadLocally = announcementId => {
    if (!announcementId) return;
    setTableData(prev =>
      prev.map(row =>
        getAnnouncementId(row) === announcementId ? { ...row, is_read: true } : row
      )
    );
  };

  const closeAnnouncementModal = () => {
    setIsAnnouncementModalOpen(false);
    setSelectedAnnouncement(null);
  };

  const onViewAnnouncement = async announcement => {
    if (!token) return;
    const announcementId = getAnnouncementId(announcement);
    const nextSelectedAnnouncement = announcement.is_read
      ? announcement
      : { ...announcement, is_read: true };
    setSelectedAnnouncement(nextSelectedAnnouncement);
    setIsAnnouncementModalOpen(true);

    if (!announcement.is_read) {
      if (!announcementId) {
        console.warn("Announcement id is missing", announcement);
        return;
      }

      markRowReadLocally(announcementId);

      try {
        await markAnnouncementRead(token, announcementId);
        sendAuditEvent({
          token,
          eventType: 10,
          route: typeof window !== "undefined" ? window.location.pathname : null,
          entityIds: [announcementId],
          metadata: getAnnouncementAuditMetadata(announcement, "read"),
        });
      } catch (err) {
        console.error("Failed to mark announcement as read", err);
        refreshList();
      }
    }
  };

  return (
    <>
      {loading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) : (
        <div>
          <ViewLayout title="Announcements">
            <AnnouncementsTable
              {...tableProps}
              forceUpdate={forceUpdate}
              setForce={setForce}
              searchQuery={{ setQuery, query, ordering, setOrdering }}
              onView={onViewAnnouncement}
            />
          </ViewLayout>
          <Pagination pagination={paginationProps} />
        </div>
      )}
      <AnnouncementDetailModal
        isOpen={isAnnouncementModalOpen}
        toggle={closeAnnouncementModal}
        announcement={selectedAnnouncement}
      />
    </>
  );
};

export default Announcements;
